// --- React ---
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 스타일 · 테마 ---
import { useTheme } from '@/theme';

// --- 공통 UI 컴포넌트 ---
import { Flex, Text, Card, CardTitle } from '@/components/ui';
import { Button, IconButton } from '@/components/ui';
import { Tabs } from '@/components/common/Tabs';
import { DataTable } from '@/components/common/DataTable';
import { DatalogTab } from '@/components/site/DatalogTab';
import { AlarmLogTab } from '@/components/site/AlarmLogTab';
import { EventLogTab } from '@/components/site/EventLogTab';
import { CalibrationBumpTab } from '@/components/site/CalibrationBumpTab';
import { ConfigurationLogTab } from '@/components/site/ConfigurationLogTab';
import LogRequestDialog from '@/components/dialogs/LogRequestDialog';
import { ConnectDevice } from '@/components/device/connectDevice/ConnectDevice';
import { CurrentTimeIndicator } from '@/components/common/CurrentTimeIndicator';

// --- API ---
import { useGetSiteDevices } from '@/api/device/useDevice';

// --- 스토어 ---
import { useSelectionStore } from '@/store/selectionStore';
import { useAppStore } from '@/store/loadingStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useConnectionStore } from '@/store/connectionStore';

// --- 훅 ---
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';
import { useDeviceValidation } from './useDeviceValidation';
import { useDatalogLogRequest } from './useDatalogLogRequest';

// --- 타입 ---
import type { ColumnDef } from '@tanstack/react-table';
import type { Device } from '@packages/types/device/dto';
import type { DeviceInfo, DeviceConfiguration } from '@packages/types/device/lite';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 유틸 ---
import { generatePdf } from '@/utils/pdfGenerator';
import { parseSiteApiError } from '@/utils/errors';

// ==================== 컴포넌트 ====================

const DatalogPage: React.FC = () => {
  const { t } = useTranslation('datalog');
  const { theme } = useTheme();
  const { showLoading, hideLoading } = useAppStore();
  const { showSuccess, showError } = useNotificationStore();
  const connectionStore = useConnectionStore();
  const { refreshDeviceInfo } = useConnectionActions();

  const { currentCompany, sites, mySite, currentSiteId, isSiteManager } = useCompanySiteScope();

  const { selectedSiteId, selectedDeviceId, setSelectedSiteId, setSelectedDeviceId } =
    useSelectionStore();

  // ==================== 상태 관리 ====================

  const hasRequestedDeviceInfoRef = React.useRef(false);
  const [isDeviceListOpen, setIsDeviceListOpen] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('datalog');
  const [exportData, setExportData] = useState<{
    chartImageUrl?: string;
    tableData: any[];
    tableColumns: Array<{ header: string; accessorKey: string }>;
  } | null>(null);

  // ==================== API 훅 ====================

  /** 현재 선택된 사이트 결정 (selectedSiteId > SiteManager 본인 사이트) */
  const currentSite = useMemo(() => {
    if (selectedSiteId && selectedSiteId !== 'all') {
      return sites.find(s => s.id === selectedSiteId) || null;
    }
    if (isSiteManager && mySite) return mySite;
    return null;
  }, [isSiteManager, mySite, selectedSiteId, sites]);

  const { data: devices = [], isLoading: loadingDevices } = useGetSiteDevices(currentSiteId, {
    enabled: currentSiteId !== null,
  });

  const selectedDevice = useMemo(() => {
    if (!selectedDeviceId) return null;
    return devices.find(d => d.id === selectedDeviceId) || null;
  }, [selectedDeviceId, devices]);

  // ==================== 커스텀 훅 ====================

  const { validateDeviceOwnership } = useDeviceValidation({ devices });
  const {
    logRequestDialog,
    openLogRequestDialog,
    closeLogRequestDialog,
    handleLogRequestConfirm,
    isCradleConnected,
    isBleConnected,
    isImportLogEnabled,
  } = useDatalogLogRequest({ devices });

  // ==================== 이펙트 ====================

  /** SiteManager가 사이트를 선택하지 않은 경우 본인 사이트 자동 선택 */
  useEffect(() => {
    if (isSiteManager && mySite && !selectedSiteId) {
      setSelectedSiteId(mySite.id);
    }
  }, [isSiteManager, mySite, selectedSiteId]);

  /** 가이드 시작 시 왼쪽 패널(Import Log · 기기 목록)을 펼친다. */
  useEffect(() => {
    const handleGuideStart = (event: Event) => {
      const detail = (event as CustomEvent).detail as { guideKey?: string } | undefined;
      if (detail?.guideKey === 'datalog') setIsDeviceListOpen(true);
    };
    window.addEventListener('bellcloud:guide:start', handleGuideStart);
    return () => window.removeEventListener('bellcloud:guide:start', handleGuideStart);
  }, []);

  /**
   * 기기 연결 후 소유권 검증.
   * 흐름:
   *   1) 기기/프로토콜이 연결 상태가 아니면 스킵
   *   2) 디바이스 목록 로딩 완료 대기
   *   3) deviceInfo로 소유권 검증 (사이트에 등록된 기기인지 확인)
   *   4) 실패 시 에러 표시 후 재시도 가능하도록 ref 리셋
   */
  const { protocol, device } = connectionStore;

  useEffect(() => {
    if (device.type === 'none') {
      hasRequestedDeviceInfoRef.current = false;
      return;
    }
    if (protocol.type === 'none') return;
    if (loadingDevices) return;
    if (hasRequestedDeviceInfoRef.current) return;

    hasRequestedDeviceInfoRef.current = true;

    const fetchAndValidateDevice = async () => {
      try {
        const deviceInfo = device.info as unknown as DeviceInfo;
        const deviceConfig = device.config as unknown as DeviceConfiguration;

        if (!deviceInfo || !deviceInfo.serial_number) {
          hasRequestedDeviceInfoRef.current = false;
          return;
        }

        await validateDeviceOwnership(deviceInfo, deviceConfig);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (
          !errorMessage.includes('bleNotInitialized') &&
          !errorMessage.includes('Not connected')
        ) {
          const e = parseSiteApiError(error);
          showError(e.title, e.message);
        }
        hasRequestedDeviceInfoRef.current = false;
      } finally {
        hideLoading();
      }
    };

    fetchAndValidateDevice();
  }, [
    device.type,
    protocol.type,
    loadingDevices,
    validateDeviceOwnership,
    refreshDeviceInfo,
    showLoading,
    hideLoading,
    showError,
  ]);

  // ==================== 핸들러 ====================

  /** 디바이스 목록에서 행 클릭 시 해당 디바이스 선택 */
  const handleDeviceClick = (clickedDevice: Device) => {
    setSelectedDeviceId(clickedDevice.id);
  };

  /** 디바이스 목록 테이블 컬럼 정의 */
  const deviceColumns: ColumnDef<Device>[] = useMemo(
    () => [
      {
        accessorKey: 'serial_number',
        header: () => t(i18nKeys.datalog.serial),
        cell: ({ row }) => <Text style={{ cursor: 'pointer' }}>{row.original.serial_number}</Text>,
        enableSorting: true,
        enableColumnFilter: true,
        size: 100,
      },
      {
        accessorKey: 'name',
        header: () => 'Worker',
        cell: ({ row }) => <Text style={{ cursor: 'pointer' }}>{row.original.name || '-'}</Text>,
        enableSorting: true,
        enableColumnFilter: true,
        size: 100,
      },
      {
        accessorKey: 'last_location',
        header: () => 'Location',
        cell: ({ row }) => (
          <Text style={{ cursor: 'pointer' }}>{row.original.last_location || '-'}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        size: 100,
      },
    ],
    [t]
  );

  /** 탭 컴포넌트에서 내보낸 차트/테이블 데이터를 PDF 생성용으로 저장 */
  const handleExportData = useCallback(
    (data: {
      chartImageUrl?: string;
      tableData: any[];
      tableColumns: Array<{ header: string; accessorKey: string }>;
    }) => {
      setExportData(data);
    },
    []
  );

  /**
   * PDF 다운로드.
   * 현재 활성 탭의 차트 이미지 + 테이블 데이터를 PDF로 변환하여 저장.
   */
  const handleDownloadPdf = useCallback(async () => {
    if (!selectedDevice) return;

    showLoading();
    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      let chartImageUrl: string | undefined;
      let tableData: any[] = [];
      let tableColumns: Array<{ header: string; accessorKey: string }> = [];

      if (exportData?.chartImageUrl) {
        chartImageUrl = exportData.chartImageUrl;
      }
      if (exportData && exportData.tableData.length > 0) {
        tableData = exportData.tableData;
        tableColumns = exportData.tableColumns;
      }

      await generatePdf({
        logType:
          activeTab === 'datalog'
            ? 'Gas Reading'
            : activeTab === 'alarmlog'
              ? 'Alarm Log'
              : activeTab === 'eventlog'
                ? 'Event Log'
                : 'Calibration & Bump',
        serialNumber: selectedDevice.serial_number,
        companyName: currentCompany?.name || '',
        siteName: currentSite?.name || '',
        chartImageUrl,
        tableData,
        tableColumns,
      });

      showSuccess(i18nKeys.datalog.pdfGenerated, i18nKeys.datalog.pdfGenerated);
    } catch (error: unknown) {
      console.error('PDF 생성 실패:', error);
      showError(i18nKeys.datalog.pdfGenerationFailed, i18nKeys.datalog.pdfGenerationFailed);
    } finally {
      hideLoading();
    }
  }, [
    selectedDevice,
    activeTab,
    exportData,
    currentCompany,
    currentSite,
    showLoading,
    hideLoading,
    showSuccess,
    showError,
    t,
  ]);

  // ==================== 렌더링 ====================

  return (
    <>
      {/* 로그 요청 다이얼로그 (BLE/크래들 연결 후 로그 가져오기 확인) */}
      <LogRequestDialog
        isOpen={logRequestDialog.isOpen}
        onClose={closeLogRequestDialog}
        onConfirm={handleLogRequestConfirm}
        deviceName={logRequestDialog.device?.cloud_name || logRequestDialog.device?.serial_number}
        deviceType={
          logRequestDialog.device?.device_type?.toLowerCase() === 'bellmini'
            ? DeviceType.MINI
            : DeviceType.LITE
        }
        currentConnectionType={
          isBleConnected ? 'bluetooth' : isCradleConnected ? 'cradle' : undefined
        }
      />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          padding: `${theme.spacing[6]}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: `${theme.spacing[6]}px`,
          background: theme.colors.surface.layout.overlay,
        }}
      >
        {currentSiteId ? (
          <div
            id="datalog-layout-container"
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'row',
              gap: theme.spacing[4],
            }}
          >
            {/* 왼쪽 패널 — 디바이스 목록 + 로그 임포트 */}
            {isDeviceListOpen && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  width: '400px',
                  gap: theme.spacing[5],
                }}
              >
                {/* 로그 임포트 카드 */}
                <Card
                  data-tour="datalog-import-log-card"
                  padding={6}
                  border="emphasis"
                  layout="flex"
                  gap={5}
                  fullWidth
                  height="auto"
                >
                  <Flex justify="between" align="center" style={{ width: '100%', flexShrink: 0 }}>
                    <CardTitle>Import Log</CardTitle>
                    <IconButton
                      onClick={() => setIsDeviceListOpen(false)}
                      aria-label="Close device list"
                    >
                      <img
                        src="/icons/slide_in.svg"
                        alt="Close"
                        style={{ width: '20px', height: '20px' }}
                      />
                    </IconButton>
                  </Flex>
                  <ConnectDevice
                    deviceType={
                      selectedDevice?.device_type === 'bellmini' ? DeviceType.MINI : DeviceType.LITE
                    }
                    cradleAble={true}
                    deviceImage={false}
                  />
                  <Button
                    data-tour="datalog-import-log-button"
                    onClick={openLogRequestDialog}
                    disabled={!isImportLogEnabled}
                    width="100%"
                  >
                    Import Log
                  </Button>
                </Card>

                {/* 등록된 디바이스 목록 카드 */}
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Card
                    data-tour="datalog-registered-devices"
                    padding={6}
                    border="emphasis"
                    layout="flex"
                    gap={5}
                    fullWidth
                    fullHeight
                  >
                    <Flex justify="between" align="center" style={{ width: '100%' }}>
                      <CardTitle>Registered Devices</CardTitle>
                      <IconButton
                        onClick={() => setIsDeviceListOpen(false)}
                        aria-label="Close device list"
                      >
                        <img
                          src="/icons/slide_in.svg"
                          alt="Close"
                          style={{ width: '20px', height: '20px' }}
                        />
                      </IconButton>
                    </Flex>
                    <div
                      style={{
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <DataTable<Device>
                        data={devices}
                        columns={deviceColumns}
                        isLoading={loadingDevices}
                        emptyMessage={t(i18nKeys.datalog.noRegisteredDevices)}
                        enableSorting={true}
                        enablePagination={true}
                        pageSize={10}
                        enableFiltering={true}
                        getRowStyle={row => ({
                          backgroundColor:
                            row.original.id === selectedDeviceId
                              ? theme.colors.button.bg.primary + '20'
                              : 'transparent',
                        })}
                        onRowClick={handleDeviceClick}
                      />
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* 오른쪽 패널 — 데이터 로그 탭 콘텐츠 */}
            {selectedDevice ? (
              <Card
                padding={6}
                border="emphasis"
                layout="flex"
                gap={5}
                fullWidth
                fullHeight
              >
                <div data-tour="datalog-log-area" style={{ width: '100%', flexShrink: 0 }}>
                  <Flex justify="between" align="center" style={{ width: '100%' }}>
                    <Flex align="center" gap={2}>
                      {!isDeviceListOpen && (
                        <IconButton
                          onClick={() => setIsDeviceListOpen(true)}
                          aria-label="Open device list"
                        >
                          <img
                            src="/icons/slide_in.svg"
                            alt="Open"
                            style={{ width: '20px', height: '20px', transform: 'rotate(180deg)' }}
                          />
                        </IconButton>
                      )}
                      <CardTitle>
                        {selectedDevice.serial_number}
                        {selectedDevice.cloud_name && ` - ${selectedDevice.cloud_name}`}
                      </CardTitle>
                    </Flex>
                    <CurrentTimeIndicator />
                  </Flex>
                </div>
                {/* 탭 영역 — Gas Reading · Events · Configuration · Event Log · Calib & Bump */}
                <div
                  data-tab-content
                  style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                >
                  <Tabs
                    groups={[
                      {
                        title: 'Datalog',
                        tabs: [
                          {
                            id: 'datalog',
                            label: 'Gas Reading',
                            content: (
                              <DatalogTab
                                device={selectedDevice}
                                deviceType={
                                  selectedDevice.device_type === 'bellmini'
                                    ? DeviceType.MINI
                                    : DeviceType.LITE
                                }
                                onExportData={handleExportData}
                                onDownloadPdf={handleDownloadPdf}
                              />
                            ),
                          },
                          {
                            id: 'alarmlog',
                            label: 'Events',
                            content: (
                              <AlarmLogTab
                                device={selectedDevice}
                                onExportData={handleExportData}
                                onDownloadPdf={handleDownloadPdf}
                              />
                            ),
                          },
                          {
                            id: 'configuration',
                            label: 'Configuration',
                            content: (
                              <ConfigurationLogTab
                                device={selectedDevice}
                                onExportData={handleExportData}
                                onDownloadPdf={handleDownloadPdf}
                              />
                            ),
                          },
                        ],
                      },
                      {
                        tabs: [
                          {
                            id: 'eventlog',
                            label: 'Event Log',
                            content: (
                              <EventLogTab
                                device={selectedDevice}
                                onExportData={handleExportData}
                                onDownloadPdf={handleDownloadPdf}
                              />
                            ),
                          },
                        ],
                      },
                      {
                        tabs: [
                          {
                            id: 'calibration-bump',
                            label: 'Calib & Bump Log',
                            content: (
                              <CalibrationBumpTab
                                device={selectedDevice}
                                operator=""
                                onDownloadPdf={handleDownloadPdf}
                              />
                            ),
                          },
                        ],
                      },
                    ]}
                    defaultActiveTab="datalog"
                    onChange={setActiveTab}
                  />
                </div>
              </Card>
            ) : (
              /* 디바이스 미선택 시 안내 메시지 */
              <Card
                padding={6}
                border="emphasis"
                layout="flex"
                gap={5}
                fullWidth
                fullHeight
              >
                <div data-tour="datalog-log-area" style={{ width: '100%', flexShrink: 0 }}>
                  <Flex justify="start" align="center" style={{ width: '100%' }}>
                    {!isDeviceListOpen && (
                      <IconButton
                        onClick={() => setIsDeviceListOpen(true)}
                        aria-label="Open device list"
                      >
                        <img
                          src="/icons/slide_in.svg"
                          alt="Open"
                          style={{ width: '20px', height: '20px', transform: 'rotate(180deg)' }}
                        />
                      </IconButton>
                    )}
                    <CardTitle>{t(i18nKeys.common.select_device)}</CardTitle>
                  </Flex>
                </div>
              </Card>
            )}
          </div>
        ) : (
          /* 사이트 미선택 시 안내 메시지 */
          <Card padding={6} border="emphasis" layout="flex" gap={5} fullWidth fullHeight>
            <Flex justify="start" align="center" style={{ width: '100%', flexShrink: 0 }}>
              {!isDeviceListOpen && (
                <IconButton onClick={() => setIsDeviceListOpen(true)} aria-label="Open device list">
                  <img
                    src="/icons/slide_in.svg"
                    alt="Open"
                    style={{ width: '20px', height: '20px', transform: 'rotate(180deg)' }}
                  />
                </IconButton>
              )}
              <CardTitle>{t(i18nKeys.common.select_site)}</CardTitle>
            </Flex>
          </Card>
        )}
      </div>
    </>
  );
};

export default DatalogPage;
